import {
  Component,
  OnInit,
  viewChild,
  inject,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { FilterMetadata, SelectItem } from '@openng/optimus-ui/api';
import { take } from 'rxjs/operators';
import { CRM_ST_DETAIL_CONTAINER } from 'src/app/constants/common.constants';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { TabService } from 'src/app/core/services/tab.service';
import { ShiftTurnover } from 'src/app/model/shift/shift-turnover';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { CrmShiftTurnoverTableService } from 'src/app/services/table/crm-shift-turnover-table.service';
import { ShiftTurnoverApiService } from 'src/app/services/api/shift-turnover-api.service';
import { ShiftTurnoverBuilderService } from 'src/app/services/builder/shift-turnover-builder.service';
import { BusinessUnitFilterComponent } from 'src/app/core/components/business-unit-filter/business-unit-filter.component';
import { getCrmShiftTurnoverColumns } from '../../definitions/crm-shift-turnover-table-definition';
import { AOR } from 'src/app/model/locations/aor';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { AorApiService } from 'src/app/services/api/aor-api.service';
import { sortByProperty } from 'src/app/utils/utils';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { DatePicker } from '@openng/optimus-ui/datepicker';
import { FormsModule } from '@angular/forms';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { ShiftGroup } from 'src/app/model/shift/shift-group';
import { ShiftTurnoverSearch } from 'src/app/model/shift/shift-turnover-search-results';

@Component({
  selector: 'app-crm-shift-turnover-container',
  templateUrl: './crm-shift-turnover-container.component.html',
  styleUrls: ['./crm-shift-turnover-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ProgressSpinnerComponent,
    BlockUI,
    DatePicker,
    FormsModule,
    ButtonDirective,
    BusinessUnitFilterComponent,
    GeneralGridComponent,
    TranslatePipe,
  ],
})
export class CrmShiftTurnoverContainerComponent
  extends SearchContainerComponent<ShiftTurnoverSearch>
  implements OnInit
{
  protected _translateService = inject(TranslateService);
  protected _shiftTurnoverApi = inject(ShiftTurnoverApiService);
  protected _shiftTurnoverTableService = inject(CrmShiftTurnoverTableService);
  protected _shiftTurnoverBuilder = inject(ShiftTurnoverBuilderService);
  protected _authApi = inject(AuthApiService);
  protected _AORApi = inject(AorApiService);
  protected _tabService = inject(TabService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _cdRef = inject(ChangeDetectorRef);

  readonly businessUnitFilter =
    viewChild<BusinessUnitFilterComponent>('buFilter');

  allShiftTurnovers: any[] = [];
  additionalFilters = [];
  actions: any[] = [];
  startDate: Date = new Date();
  endDate: Date = new Date();
  availableDistricts: SelectItem[] = [];
  groups: ShiftGroup[] = [];
  groupId!: string;
  shiftTypes: SelectItem[] = [];
  statusOptions: SelectItem[] = [];
  groupName = 'Gas Control Room';
  init = false;
  private _isRefreshing = false;

  constructor() {
    super();
  }

  ngOnInit() {
    this.setTableService(this._shiftTurnoverTableService);
    this.initialize();
  }

  initialize() {
    this.statusOptions = [
      { label: 'Choose', value: null },
      { label: 'New', value: 'N' },
      { label: 'Departing Complete', value: 'DC' },
      { label: 'Resolved', value: 'R' },
    ];
    this._translateService
      .get('CRM.SCREEN.SHIFT_TURNOVER')
      .subscribe((label) => {
        this._tabService.updateActiveTabLabel(label);
      });

    this._shiftTurnoverApi.getShiftGroups().subscribe((groups) => {
      this.groups = groups;
      this.groupId = this.groups.find((g) => g.name === this.groupName)!.id;
      this.applyPreviousFilters();
    });

    this._shiftTurnoverApi.getShiftTypes().subscribe((types) => {
      this.shiftTypes = [
        { label: 'Choose', value: null },
        ...types
          .filter((d) => d.ShiftGroup.name === 'Gas Control Room')
          .map((d) => ({ label: d.name, value: d.id })),
      ];
      this.setupTable();
    });

    this._AORApi
      .getAORs(null)
      .pipe(take(1))
      .subscribe({
        next: (aors) => {
          this.availableDistricts = [
            { label: 'Choose', value: null },
            ...aors
              .sort((a: AOR, b: AOR) => sortByProperty(a, b, 'name'))
              .map((d: AOR) => ({ label: d.name, value: d.id })),
          ];
          this.setupTable();
        },
      });
  }

  setupTable() {
    this.columns = getCrmShiftTurnoverColumns(
      this.availableDistricts,
      this.shiftTypes,
      this.statusOptions
    );

    const existingFilters = this._shiftTurnoverTableService.getLastQuery();
    if (!existingFilters || !existingFilters['startDate']) {
      this.goToToday();
    } else {
      this.startDate = existingFilters['startDate'];
      this.endDate = existingFilters['endDate'];
      this.dateUpdate();
    }

    this.init = true;
  }

  goToToday(invoke = true) {
    const today = new Date();
    this.startDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      6,
      0,
      0
    );

    this.endDate = new Date();
    this.endDate.setDate(this.startDate.getDate() + 1);
    this.endDate = new Date(
      this.endDate.getFullYear(),
      this.endDate.getMonth(),
      this.endDate.getDate(),
      5,
      59,
      59
    );
    if (invoke) {
      this.dateUpdate();
    }
  }

  previousDay() {
    const next = new Date(this.startDate.valueOf());
    next.setDate(this.startDate.getDate() - 1);
    next.setHours(6);
    next.setMinutes(0);
    next.setSeconds(0);
    this.startDate = new Date(next.valueOf());

    this.endDate = new Date(next.valueOf());
    this.endDate.setDate(this.endDate.getDate() + 1);
    this.endDate.setHours(5);
    this.endDate.setMinutes(59);
    this.endDate.setSeconds(59);
    this.dateUpdate();
  }

  nextDay() {
    const next = new Date(this.startDate.valueOf());
    next.setDate(this.startDate.getDate() + 1);
    next.setHours(6);
    next.setMinutes(0);
    next.setSeconds(0);
    this.startDate = new Date(next.valueOf());

    this.endDate = new Date(next.valueOf());
    this.endDate.setDate(this.endDate.getDate() + 1);
    this.endDate.setHours(5);
    this.endDate.setMinutes(59);
    this.endDate.setSeconds(59);
    this.dateUpdate();
  }

  dateUpdate(fromEnd = false) {
    if (this.endDate < this.startDate && fromEnd === false) {
      this.endDate = new Date(this.startDate.valueOf());
      this.endDate.setDate(this.endDate.getDate() + 1);
      this.endDate.setHours(5);
      this.endDate.setMinutes(59);
      this.endDate.setSeconds(59);
    } else if (this.endDate < this.startDate && fromEnd === true) {
      this.startDate = new Date(this.endDate.valueOf());
      this.startDate.setDate(this.startDate.getDate() - 1);
      this.startDate.setHours(6);
      this.startDate.setMinutes(0);
      this.startDate.setSeconds(0);
    }

    this.lazyLoad({ lazy: this._shiftTurnoverTableService.getLastLazyLoad()! });
    this._cdRef.detectChanges();
  }

  changeViewEvent($event: { data: ShiftTurnover }) {
    const tab = this._tabService.buildNewTab(
      CRM_ST_DETAIL_CONTAINER,
      true,
      undefined,
      $event.data.id
    );
    this._tabService.openTab(tab);
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    if (!this.startDate) {
      this.goToToday(false);
    }
    let selectedBusinessUnits = [];
    const businessUnitFilter = this.businessUnitFilter();
    if (businessUnitFilter?.selectedBusinessUnits) {
      // Handle further refreshes & changes to business unit filter component
      selectedBusinessUnits = businessUnitFilter.selectedBusinessUnits;
    } else {
      // Handle initial page load (before the business unit filter subcomponent exists)
      selectedBusinessUnits = this._authApi
        .getUserBusinessUnits()
        .map((bu) => bu.id);
    }
    const query = {
      startDate: this.startDate,
      endDate: this.endDate,
      groupId: this.groupId,
      aor: filters.aorName
        ? (filters.aorName as FilterMetadata).value.trim()
        : null,
      shiftTypeId: filters.shiftName
        ? (filters.shiftName as FilterMetadata).value
        : null,
      status: filters.statusLabel
        ? (filters.statusLabel as FilterMetadata).value.trim()
        : null,
      incoming: filters.incomingName
        ? (filters.incomingName as FilterMetadata).value.trim()
        : null,
      departing: filters.departingName
        ? (filters.departingName as FilterMetadata).value.trim()
        : null,
      businessUnits: selectedBusinessUnits,
    };
    this._shiftTurnoverTableService.setLastQuery(query);
    this.queryNetwork(req, $event, query);
  }

  handleBusinessUnitChange() {
    this.lazyLoad({ lazy: this._shiftTurnoverTableService.getLastLazyLoad()! });
  }

  private queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ) {
    req.page += 1;
    const sort: any = {};
    if ($event.lazy && $event.lazy.sortField) {
      let field = $event.lazy.sortField;
      if ($event.lazy.sortField === 'incomingName') {
        field = 'incoming';
      } else if ($event.lazy.sortField === 'departingName') {
        field = 'departing';
      } else if ($event.lazy.sortField === 'statusLabel') {
        field = 'status';
      } else if ($event.lazy.sortField === 'aorName') {
        field = 'aor';
      }
      sort[field as string] =
        $event.lazy.sortOrder && $event.lazy.sortOrder === 1 ? 'DESC' : 'ASC';
      this._shiftTurnoverTableService.setLastSortField(
        $event.lazy.sortField as string
      );
      this._shiftTurnoverTableService.setLastSortDirection(
        $event.lazy.sortOrder!
      );
    } else {
      sort['shiftDate'] = 'DESC';
      this._shiftTurnoverTableService.setLastSortField('shiftDate');
      this._shiftTurnoverTableService.setLastSortDirection(0);
    }
    if (!this._isRefreshing) {
      this._isRefreshing = true;
      this._shiftTurnoverApi
        .queryForShiftTurnovers(req.pageSize, req.page, query, sort)
        .subscribe({
          next: (connection) => {
            const shifts = connection.items;
            this.totalRecords = connection.totalRecords;
            this.elements = [...shifts];
            this.loading = false;
            this._isRefreshing = false;
            this._cdRef.detectChanges();
          },
          error: (error) => {
            console.error(error);
            this._logAndMessage.translateToErrorAlert({
              headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
              bodyKey: 'CRM.MESSAGES.ERROR.LOAD_SHIFT_TURNOVERS',
            });
            this.totalRecords = 0;
            this.elements = [];
            this.loading = false;
            this._isRefreshing = false;
          },
        });
    }
  }

  newShiftChange() {
    const st = this._shiftTurnoverBuilder.buildNullShiftTurnover(this.groupId);
    this._shiftTurnoverTableService.setSelected(st);
    const tab = this._tabService.buildNewTab(CRM_ST_DETAIL_CONTAINER, true);
    this._tabService.openTab(tab);
  }

  refresh(): void {
    this.lazyLoad({ lazy: this._shiftTurnoverTableService.getLastLazyLoad()! });
  }
}
